import { oc } from "@orpc/contract";
import { z } from "zod";

const TestSuiteRunSummarySchema = z.object({
  totalCases: z.number(),
  activeCases: z.number(),
  skipped: z.number(),
  passed: z.number(),
  failed: z.number(),
  pendingReview: z.number(),
  accuracy: z.number(),
  regressions: z.number(),
  improvements: z.number(),
});

const TestSuiteCaseSchema = z.object({
  id: z.uuid(),
  testSuiteId: z.uuid(),
  testSuiteVersionId: z.uuid().nullable().optional(),
  inputData: z.record(z.string(), z.unknown()),
  expectedOutput: z.record(z.string(), z.unknown()),
  sourceStepRunId: z.uuid().nullable().optional(),
  isActive: z.boolean(),
  notes: z.string().nullable().optional(),
  createdBy: z.uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type TestSuiteCaseResponse = z.infer<typeof TestSuiteCaseSchema>;

const TestSuiteSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  workflowDefinitionId: z.uuid(),
  workflowStepId: z.uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  pinnedVersionId: z.uuid().nullable().optional(),
  createdBy: z.uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type TestSuiteResponse = z.infer<typeof TestSuiteSchema>;

const TestSuiteVersionSchema = z.object({
  id: z.uuid(),
  testSuiteId: z.uuid(),
  parentVersionId: z.uuid().nullable().optional(),
  status: z.enum(["draft", "finalized"]),
  version: z.number(),
  changelog: z.string().nullable().optional(),
  isPinned: z.boolean(),
  createdBy: z.uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type TestSuiteVersionResponse = z.infer<typeof TestSuiteVersionSchema>;

const TestSuiteWithCasesSchema = TestSuiteSchema.extend({
  cases: z.array(TestSuiteCaseSchema),
  // Workflow version a default run will score (the workflow's pinned version).
  pinnedWorkflowVersion: z.number().nullable().optional(),
});

const TestCaseFieldDiffSchema = z.object({
  expected: z.unknown(),
  actual: z.unknown(),
  kind: z.enum(["missing", "unexpected", "mismatch"]),
});

const TestCaseDiffSchema = z.record(z.string(), TestCaseFieldDiffSchema);

export type TestCaseDiffResponse = z.infer<typeof TestCaseDiffSchema>;

const TestCaseResultSchema = z.object({
  id: z.uuid(),
  testSuiteRunId: z.uuid(),
  testCaseId: z.uuid(),
  actualOutput: z.record(z.string(), z.unknown()).nullable().optional(),
  wasSkipped: z.boolean(),
  autoScore: z.enum(["pass", "fail", "unscorable"]).nullable().optional(),
  humanVerdict: z.enum(["pass", "fail", "skip"]).nullable().optional(),
  executionTimeMs: z.number().nullable().optional(),
  error: z.string().nullable().optional(),
  diff: TestCaseDiffSchema.nullable().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type TestCaseResultResponse = z.infer<typeof TestCaseResultSchema>;

const TestSuiteRunSchema = z.object({
  id: z.uuid(),
  testSuiteId: z.uuid(),
  testSuiteVersionId: z.uuid().nullable().optional(),
  workflowDefinitionId: z.uuid().nullable().optional(),
  workflowVersion: z.number().nullable().optional(),
  stepConfigSnapshot: z.record(z.string(), z.unknown()),
  configOverrides: z.record(z.string(), z.unknown()).nullable().optional(),
  configVariantLabel: z.string().nullable().optional(),
  status: z.enum(["queued", "running", "completed", "failed"]),
  summary: TestSuiteRunSummarySchema.nullable().optional(),
  previousRunId: z.uuid().nullable().optional(),
  triggeredBy: z.uuid(),
  startedAt: z.iso.datetime().nullable().optional(),
  completedAt: z.iso.datetime().nullable().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type TestSuiteRunResponse = z.infer<typeof TestSuiteRunSchema>;

const TestSuiteRunWithResultsSchema = TestSuiteRunSchema.extend({
  totalCases: z.number(),
  caseResults: z.array(
    TestCaseResultSchema.extend({
      testCase: TestSuiteCaseSchema.optional(),
      regressionStatus: z
        .enum(["regression", "improvement", "unchanged", "new"])
        .optional(),
    }),
  ),
});

const TestSuiteTrendPointSchema = z.object({
  runId: z.uuid(),
  testSuiteVersionId: z.uuid().nullable().optional(),
  suiteVersion: z.number().nullable().optional(),
  workflowDefinitionId: z.uuid().nullable().optional(),
  workflowVersion: z.number().nullable().optional(),
  accuracy: z.number().nullable().optional(),
  passed: z.number(),
  failed: z.number(),
  regressions: z.number(),
  improvements: z.number(),
  configVariantLabel: z.string().nullable().optional(),
  completedAt: z.iso.datetime().nullable().optional(),
});

export type TestSuiteTrendPointResponse = z.infer<
  typeof TestSuiteTrendPointSchema
>;

const ProposedTestCaseSchema = z.object({
  inputData: z.record(z.string(), z.unknown()),
  proposedExpectedOutput: z.record(z.string(), z.unknown()).optional(),
  sourceStepRunId: z.uuid().optional(),
  sourceWorkflowRunId: z.uuid().optional(),
  source: z.enum(["step_run", "workflow_emails"]),
  label: z.string().optional(),
  stepRunCreatedAt: z.iso.datetime(),
});

export const testSuiteContracts = {
  listTestSuites: oc
    .input(z.object({ workflowStepId: z.uuid() }))
    .output(z.object({ testSuites: z.array(TestSuiteSchema) }))
    .route({ method: "GET" }),

  getTestSuite: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ testSuite: TestSuiteWithCasesSchema }))
    .route({ method: "GET" }),

  createTestSuite: oc
    .input(
      z.object({
        workflowDefinitionId: z.uuid(),
        workflowStepId: z.uuid(),
        name: z.string().min(1).max(255),
        description: z.string().optional(),
      }),
    )
    .output(z.object({ testSuite: TestSuiteSchema }))
    .route({ method: "POST" }),

  updateTestSuite: oc
    .input(
      z.object({
        id: z.uuid(),
        name: z.string().min(1).max(255).optional(),
        description: z.string().nullable().optional(),
      }),
    )
    .output(z.object({ testSuite: TestSuiteSchema }))
    .route({ method: "PUT" }),

  deleteTestSuite: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({ method: "DELETE" }),

  addTestCases: oc
    .input(
      z.object({
        testSuiteId: z.uuid(),
        cases: z.array(
          z.object({
            inputData: z.record(z.string(), z.unknown()),
            expectedOutput: z.record(z.string(), z.unknown()),
            sourceStepRunId: z.uuid().optional(),
            notes: z.string().optional(),
          }),
        ),
      }),
    )
    .output(z.object({ cases: z.array(TestSuiteCaseSchema) }))
    .route({ method: "POST" }),

  updateTestCase: oc
    .input(
      z.object({
        id: z.uuid(),
        inputData: z.record(z.string(), z.unknown()).optional(),
        expectedOutput: z.record(z.string(), z.unknown()).optional(),
        isActive: z.boolean().optional(),
        notes: z.string().nullable().optional(),
      }),
    )
    .output(z.object({ testCase: TestSuiteCaseSchema }))
    .route({ method: "PUT" }),

  deleteTestCase: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({ method: "DELETE" }),

  generateTestCasesFromHistory: oc
    .input(
      z.object({
        workflowStepId: z.uuid(),
        limit: z.number().min(1).max(100).optional(),
        // Optional dotted JSON path against StepRun.inputData with an
        // equality check. Used by step types whose input encodes a
        // user-meaningful selector — e.g. extract_document_content_v2
        // filters historical runs by `extractionConfig.schemaDefinitionId`
        // so the seed set only contains cases that used the schema being
        // tested.
        inputFilter: z
          .object({
            jsonPath: z.string().min(1),
            equals: z.union([z.string(), z.number(), z.boolean()]),
          })
          .optional(),
      }),
    )
    .output(z.object({ proposedCases: z.array(ProposedTestCaseSchema) }))
    .route({ method: "POST" }),

  runTestSuite: oc
    .input(
      z.object({
        testSuiteId: z.uuid(),
        configOverrides: z.record(z.string(), z.unknown()).optional(),
        configVariantLabel: z.string().max(255).optional(),
        // Workflow version to score. Defaults to the suite's origin
        // definition; pass a sibling version to trend the same cases across
        // workflow versions.
        workflowDefinitionId: z.uuid().optional(),
      }),
    )
    .output(z.object({ testSuiteRun: TestSuiteRunSchema }))
    .route({ method: "POST" }),

  listTestSuiteVersions: oc
    .input(z.object({ testSuiteId: z.uuid() }))
    .output(z.object({ versions: z.array(TestSuiteVersionSchema) }))
    .route({ method: "GET" }),

  forkTestSuiteVersion: oc
    .input(
      z.object({
        testSuiteId: z.uuid(),
        sourceVersionId: z.uuid().optional(),
      }),
    )
    .output(z.object({ version: TestSuiteVersionSchema }))
    .route({ method: "POST" }),

  finalizeTestSuiteVersion: oc
    .input(
      z.object({
        versionId: z.uuid(),
        // Optional free-text note describing what changed in this version.
        changelog: z.string().max(10000).optional(),
      }),
    )
    .output(z.object({ version: TestSuiteVersionSchema }))
    .route({ method: "POST" }),

  publishTestSuiteVersion: oc
    .input(z.object({ testSuiteId: z.uuid(), versionId: z.uuid() }))
    .output(z.object({ testSuite: TestSuiteSchema }))
    .route({ method: "POST" }),

  updateTestSuiteVersionChangelog: oc
    .input(z.object({ versionId: z.uuid(), changelog: z.string().max(10000) }))
    .output(z.object({ version: TestSuiteVersionSchema }))
    .route({ method: "PUT" }),

  getTestSuiteTrends: oc
    .input(z.object({ testSuiteId: z.uuid() }))
    .output(z.object({ points: z.array(TestSuiteTrendPointSchema) }))
    .route({ method: "GET" }),

  getTestSuiteRun: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ testSuiteRun: TestSuiteRunWithResultsSchema }))
    .route({ method: "GET" }),

  listTestSuiteRuns: oc
    .input(z.object({ testSuiteId: z.uuid() }))
    .output(z.object({ runs: z.array(TestSuiteRunSchema) }))
    .route({ method: "GET" }),

  submitHumanVerdict: oc
    .input(
      z.object({
        testCaseResultId: z.uuid(),
        verdict: z.enum(["pass", "fail", "skip"]),
      }),
    )
    .output(z.object({ testCaseResult: TestCaseResultSchema }))
    .route({ method: "POST" }),
  suggestConfigImprovements: oc
    .input(z.object({ testSuiteRunId: z.uuid() }))
    .output(
      z.object({
        suggestions: z.array(
          z.object({
            title: z.string(),
            description: z.string(),
            suggestedOverrides: z.record(z.string(), z.unknown()),
          }),
        ),
      }),
    )
    .route({ method: "POST" }),
} as const;
